import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import { NavBar } from "../components/NavBar";
import { Footer } from "../components/Footer";
import { getCases, getCaseList } from "../data/cases";
import { useLanguage } from "../context/LanguageContext";
import { Sparkle } from "../components/Sparkle";
import { Seo } from "../components/Seo";

const KIND_ICON = {
    situation: AssignmentRoundedIcon,
    team: GroupsRoundedIcon,
    discovery: SearchRoundedIcon,
    onsite: SearchRoundedIcon,
    users: GroupsRoundedIcon,
    userstories: LightbulbRoundedIcon,
    principles: HandymanRoundedIcon,
    testing: ScienceRoundedIcon,
    system: LayersRoundedIcon,
    learnings: AutoAwesomeRoundedIcon,
};

const Eyebrow = ({ children }) => (
    <div
        className="font-mono-archive text-[11px] mb-5"
        style={{ color: "var(--muted-ink)", letterSpacing: "0.3em" }}
    >
        {children}
    </div>
);

const SectionHeading = ({ kind, children }) => {
    const Icon = KIND_ICON[kind] || AutoAwesomeRoundedIcon;
    return (
        <div className="flex flex-col items-center text-center mb-7">
            <span
                className="flex items-center justify-center w-11 h-11 rounded-2xl mb-5"
                style={{
                    background: "var(--peach-soft)",
                    color: "var(--ink)",
                    border: "none",
                }}
            >
                <Icon sx={{ fontSize: 20 }} />
            </span>
            <h2
                className="font-serif-display text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.05]"
                style={{ color: "var(--ink)" }}
            >
                {children}
            </h2>
        </div>
    );
};

const Paragraph = ({ children }) => (
    <p
        className="text-[17px] leading-[1.8] max-w-[62ch] mx-auto text-left"
        style={{ color: "var(--ink-soft)" }}
    >
        {children}
    </p>
);

const Callout = ({ children, label }) => (
    <div
        className="mt-8 rounded-[22px] p-7 sm:p-9 grain-overlay relative max-w-[720px] mx-auto text-center"
        style={{
            background:
                "linear-gradient(135deg, var(--peach-soft) 0%, var(--peach) 100%)",
        }}
    >
        <span
            className="font-mono-archive text-[10px]"
            style={{ color: "var(--ink-soft)" }}
        >
            {label}
        </span>
        <p
            className="font-serif-display text-[22px] sm:text-[28px] leading-[1.3] mt-3"
            style={{ color: "var(--ink)" }}
        >
            {children}
        </p>
    </div>
);

const RoleGrid = ({ roles }) => (
    <div className="mt-8 grid sm:grid-cols-2 gap-5 max-w-[720px] mx-auto">
        {roles.map((r) => (
            <div
                key={r.who}
                className="rounded-[16px] p-5 text-left"
                style={{
                    background: "var(--paper)",
                    border: "none",
                }}
            >
                <div
                    className="font-mono-archive text-[10.5px] mb-2"
                    style={{
                        color: "var(--yellow-deep)",
                        letterSpacing: "0.22em",
                    }}
                >
                    {r.who.toUpperCase()}
                </div>
                <div
                    className="text-[14.5px] leading-[1.6]"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {r.what}
                </div>
            </div>
        ))}
    </div>
);

const MethodChips = ({ methods }) => (
    <div className="mt-7 flex flex-wrap justify-center gap-2 max-w-[720px] mx-auto">
        {methods.map((m) => (
            <span
                key={m}
                className="font-mono-archive text-[10.5px] px-3 py-1.5 rounded-full"
                style={{
                    border: "none",
                    color: "var(--ink-soft)",
                    letterSpacing: "0.16em",
                    background: "rgba(255,255,255,0.5)",
                }}
            >
                {m.toUpperCase()}
            </span>
        ))}
    </div>
);

const PrinciplesGrid = ({ items }) => (
    <div className="mt-8 grid sm:grid-cols-2 gap-5 max-w-[760px] mx-auto">
        {items.map((p, i) => (
            <div
                key={p.title}
                className="rounded-[18px] p-6 text-left"
                style={{
                    background: "var(--paper)",
                    border: "none",
                }}
            >
                <div className="flex items-baseline gap-3">
                    <span
                        className="font-serif-display text-[28px]"
                        style={{ color: "var(--yellow-deep)" }}
                    >
                        0{i + 1}
                    </span>
                    <h4 className="font-serif-display text-[22px]">
                        {p.title}
                    </h4>
                </div>
                <p
                    className="mt-3 text-[14.5px] leading-[1.6]"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {p.body}
                </p>
            </div>
        ))}
    </div>
);

const InsightBox = ({ children, label }) => (
    <div
        className="mt-7 rounded-[18px] p-6 max-w-[720px] mx-auto text-center"
        style={{
            background: "var(--paper)",
        }}
    >
        <div
            className="font-mono-archive text-[10px] mb-2"
            style={{ color: "var(--yellow-deep)" }}
        >
            {label}
        </div>
        <p
            className="font-serif-display text-[20px] leading-[1.45] italic"
            style={{ color: "var(--ink)" }}
        >
            {children}
        </p>
    </div>
);

const UserQuote = ({ q, author }) => (
    <figure
        className="mt-10 rounded-[22px] p-8 sm:p-10 relative max-w-[720px] mx-auto text-center"
        style={{
            background: "var(--ink)",
            color: "var(--paper)",
        }}
    >
        <FormatQuoteRoundedIcon
            sx={{ fontSize: 32, opacity: 0.65, mb: 1.5 }}
            style={{ color: "var(--peach)" }}
        />
        <blockquote
            className="font-serif-display text-[24px] sm:text-[30px] leading-[1.35] italic"
        >
            “{q}”
        </blockquote>
        <figcaption
            className="mt-5 font-mono-archive text-[11px]"
            style={{
                color: "rgba(250,245,236,0.6)",
                letterSpacing: "0.22em",
            }}
        >
            — {author.toUpperCase()}
        </figcaption>
    </figure>
);

const GroupGrid = ({ groups, label }) => (
    <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-[760px] mx-auto">
        {groups.map((g, i) => (
            <div
                key={g.title}
                className="rounded-[20px] p-7 grain-overlay relative text-left"
                style={{
                    background:
                        i === 0
                            ? "var(--paper)"
                            : "linear-gradient(135deg, var(--peach-soft) 0%, var(--peach) 100%)",
                    border: "none",
                }}
            >
                <div
                    className="font-mono-archive text-[10.5px] mb-3"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {label} 0{i + 1}
                </div>
                <h4 className="font-serif-display text-[28px] mb-3">
                    {g.title}
                </h4>
                <p
                    className="text-[14.5px] leading-[1.65]"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {g.body}
                </p>
            </div>
        ))}
    </div>
);

const CriteriaGrid = ({ items }) => (
    <div className="mt-8 grid sm:grid-cols-3 gap-5 max-w-[760px] mx-auto">
        {items.map((c) => (
            <div
                key={c.title}
                className="rounded-[18px] p-6 text-left"
                style={{
                    background: "var(--paper)",
                    border: "none",
                }}
            >
                <h4 className="font-serif-display text-[22px]">{c.title}</h4>
                <p
                    className="mt-2 text-[14px] leading-[1.6]"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {c.body}
                </p>
            </div>
        ))}
    </div>
);

const Section = ({ s, labels }) => (
    <section className="py-12 lg:py-16">
        <SectionHeading kind={s.kind}>{s.heading}</SectionHeading>
        {s.body && <Paragraph>{s.body}</Paragraph>}
        {s.callout && <Callout label={labels.assignment}>{s.callout}</Callout>}
        {s.roles && <RoleGrid roles={s.roles} />}
        {s.quote && (
            <p
                className="mt-7 font-serif-display italic text-[20px] leading-[1.55] max-w-[680px] mx-auto text-center"
                style={{ color: "var(--ink)" }}
            >
                “{s.quote}”
            </p>
        )}
        {s.methods && <MethodChips methods={s.methods} />}
        {s.principles && <PrinciplesGrid items={s.principles} />}
        {s.criteria && <CriteriaGrid items={s.criteria} />}
        {s.method && (
            <div className="mt-7 max-w-[720px] mx-auto text-center">
                <div
                    className="font-mono-archive text-[10px] mb-2"
                    style={{ color: "var(--muted-ink)" }}
                >
                    {labels.method}
                </div>
                <p
                    className="text-[17px] leading-[1.8] max-w-[62ch] mx-auto text-left"
                    style={{ color: "var(--ink-soft)" }}
                >
                    {s.method}
                </p>
            </div>
        )}
        {s.groups && <GroupGrid groups={s.groups} label={labels.usergroup} />}
        {s.insight && <InsightBox label={labels.insight}>{s.insight}</InsightBox>}
        {s.pillars && <PrinciplesGrid items={s.pillars} />}
        {s.impact && (
            <p
                className="mt-8 font-serif-display text-[24px] italic max-w-[680px] mx-auto text-center"
                style={{ color: "var(--terracotta-deep)" }}
            >
                {s.impact}
            </p>
        )}
        {s.items && (
            <ol className="mt-8 grid sm:grid-cols-2 gap-6 max-w-[760px] mx-auto">
                {s.items.map((it, i) => (
                    <li
                        key={it.title}
                        className="rounded-[18px] p-6 text-left"
                        style={{
                            background: "var(--paper)",
                            border: "none",
                        }}
                    >
                        <div className="flex items-baseline gap-3 mb-2">
                            <span
                                className="font-serif-display text-[26px]"
                                style={{ color: "var(--yellow-deep)" }}
                            >
                                0{i + 1}
                            </span>
                            <h4 className="font-serif-display text-[22px]">
                                {it.title}
                            </h4>
                        </div>
                        <p
                            className="text-[14.5px] leading-[1.6]"
                            style={{ color: "var(--ink-soft)" }}
                        >
                            {it.body}
                        </p>
                    </li>
                ))}
            </ol>
        )}
        {s.userQuote && <UserQuote q={s.userQuote.quote} author={s.userQuote.author} />}
    </section>
);

const CaseDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { lang } = useLanguage();
    const data = getCases(lang)[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!data) {
        return (
            <main data-testid="case-not-found">
                <NavBar />
                <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
                    <h1 className="font-serif-display text-[48px]">
                        Case not found
                    </h1>
                    <p className="mt-4" style={{ color: "var(--ink-soft)" }}>
                        The case you’re looking for has been retired or never
                        existed.
                    </p>
                    <Link
                        to="/"
                        className="pill pill-primary mt-8 inline-flex"
                    >
                        Back to portfolio
                        <NorthEastRoundedIcon sx={{ fontSize: 14 }} />
                    </Link>
                </div>
                <Footer />
            </main>
        );
    }

    const other = getCaseList(lang).find((c) => c.slug !== slug);

    const seoTitle = `${data.title} ${data.subtitle}`.trim();
    const seoDesc = data.intro;
    const seoStructured = {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: seoTitle,
        description: seoDesc,
        author: {
            "@type": "Person",
            name: "Charlyn De Wulf",
            url: "https://charlyndewulf.be/",
        },
        inLanguage: lang === "nl" ? "nl-BE" : "en",
        url: `https://charlyndewulf.be/cases/${slug}`,
        image: data.hero,
        keywords: [
            "UX case study",
            "Charlyn De Wulf",
            "UX design",
            "design system",
            data.sector,
        ].join(", "),
    };

    return (
        <main data-testid={`case-detail-${slug}`}>
            <Seo
                title={seoTitle}
                description={seoDesc}
                path={`/cases/${slug}`}
                image={data.hero}
                type="article"
                lang={lang}
                structuredData={seoStructured}
            />
            <NavBar />

            {/* Case hero */}
            <header className="relative">
                <div className="max-w-[1320px] mx-auto px-6 lg:px-12 pt-10 pb-6">
                    <button
                        onClick={() => navigate("/#cases")}
                        data-testid="case-back"
                        className="archive-link font-mono-archive text-[11px] inline-flex items-center gap-2"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
                        {data.labels.back}
                    </button>
                </div>

                <div className="max-w-[1320px] mx-auto px-6 lg:px-12 pt-6 pb-14">
                    <div className="max-w-[820px] mx-auto text-center">
                    <Eyebrow>{data.eyebrow}</Eyebrow>
                    <h1
                        className="font-serif-display text-[44px] sm:text-[68px] lg:text-[88px] leading-[0.98]"
                        style={{ color: "var(--ink)" }}
                    >
                        {data.title}
                        <br />
                        <span
                            className="italic"
                            style={{ color: "var(--terracotta)" }}
                        >
                            {data.subtitle}.
                        </span>
                    </h1>

                    <p
                        className="mt-8 mx-auto max-w-[640px] text-[17px] leading-[1.75]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        {data.intro}
                    </p>

                    <dl
                        className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-[720px] mx-auto pt-7 text-left"
                        style={{}}
                    >
                        {[
                            [data.labels.sector, data.sector],
                            [data.labels.myrole, data.role],
                            [data.labels.type, data.type],
                            [data.labels.approach, data.approach],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <dt
                                    className="font-mono-archive text-[10px]"
                                    style={{ color: "var(--muted-ink)" }}
                                >
                                    {k}
                                </dt>
                                <dd
                                    className="font-serif-display text-[18px] italic mt-1.5"
                                    style={{ color: "var(--ink)" }}
                                >
                                    {v}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    </div>
                </div>

                <div className="max-w-[1320px] mx-auto px-6 lg:px-12">
                    <div
                        className="aspect-[16/8] rounded-[28px] overflow-hidden grain-overlay relative"
                        style={{ background: "var(--cream-deep)" }}
                    >
                        <img
                            src={data.hero}
                            alt={data.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </header>

            {/* Sections */}
            <div className="max-w-[820px] mx-auto px-6 lg:px-12 pt-6 pb-12">
                {data.sections.map((s, i) => (
                    <Section key={`${s.kind}-${i}`} s={s} labels={data.labels} />
                ))}
            </div>

            {/* Next case */}
            {other && (
                <section
                    className="relative"
                    style={{ background: "var(--paper)" }}
                >
                    <div className="max-w-[1320px] mx-auto px-6 lg:px-12 py-20">
                        <div
                            className="font-mono-archive text-[11px] mb-6 inline-flex items-center gap-2"
                            style={{ color: "var(--muted-ink)" }}
                        >
                            <Sparkle size={12} />
                            {data.labels.other}
                        </div>
                        <Link
                            to={`/cases/${other.slug}`}
                            data-testid="case-next"
                            className="group grid lg:grid-cols-12 gap-8 items-center"
                        >
                            <div className="lg:col-span-5">
                                <div
                                    className="aspect-[5/4] rounded-[22px] overflow-hidden"
                                    style={{ background: "var(--cream-deep)" }}
                                >
                                    <img
                                        src={other.image}
                                        alt={other.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-7">
                                <h3
                                    className="font-serif-display text-[40px] sm:text-[56px] leading-[1.02]"
                                    style={{ color: "var(--ink)" }}
                                >
                                    {other.title}
                                    <br />
                                    <span
                                        className="italic"
                                        style={{ color: "var(--terracotta)" }}
                                    >
                                        {other.subtitle}.
                                    </span>
                                </h3>
                                <p
                                    className="mt-4 max-w-[520px] text-[15.5px] leading-[1.7]"
                                    style={{ color: "var(--ink-soft)" }}
                                >
                                    {other.teaser}
                                </p>
                                <span className="pill pill-primary mt-6 inline-flex">
                                    {data.labels.otherCta}
                                    <NorthEastRoundedIcon sx={{ fontSize: 14 }} />
                                </span>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            <Footer />
        </main>
    );
};

export default CaseDetail;
